import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { logisticsApi } from '../../../api/logistics.js';
import { useAuth } from '../../../auth/AuthProvider.jsx';
import { PageHeader } from '../../../components/ui/PageHeader.jsx';
import { Card } from '../../../components/ui/Card.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { Badge } from '../../../components/ui/Badge.jsx';
import { Skeleton } from '../../../components/ui/Skeleton.jsx';
import { t } from '../../../lib/i18n.js';

import { TransferFormModal } from './TransferFormModal.jsx';
import { LoadFormModal } from './LoadFormModal.jsx';
import { LogisticsDetailModal } from './LogisticsDetailModal.jsx';

const TRANSFER_STATUS = { draft: ['Bozza', 'neutral'], requested: ['Richiesto', 'info'], in_transit: ['In transito', 'warning'], received: ['Ricevuto', 'success'], cancelled: ['Annullato', 'neutral'] };
const LOAD_STATUS = { draft: ['Bozza', 'neutral'], confirmed: ['Confermato', 'success'], cancelled: ['Annullato', 'neutral'] };

function storeName(s) { return s && (s.name || s.code) ? (s.name || s.code) : '-'; }

function TransfersTab({ canCreate, canUpdate }) {
  const queryClient = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const query = useQuery({ queryKey: ['transfers', 'list'], queryFn: () => logisticsApi.listTransfers({ limit: 50 }) });
  const items = (query.data && query.data.items) || [];
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['transfers'] });

  const act = useMutation({
    mutationFn: ({ id, action }) => (action === 'ship' ? logisticsApi.shipTransfer(id) : action === 'receive' ? logisticsApi.receiveTransfer(id) : logisticsApi.cancelTransfer(id)),
    onSuccess: () => { toast.success('Trasferimento aggiornato.'); invalidate(); },
    onError: (err) => toast.error(err.message || t('common.error'))
  });

  return (
    <div className="flex flex-col gap-4">
      {canCreate && <div className="flex justify-end"><Button onClick={() => setFormOpen(true)}>Nuovo trasferimento</Button></div>}
      {query.isLoading ? <Skeleton className="h-40 w-full" /> : items.length === 0 ? (
        <Card><p className="text-sm text-ink-muted">Nessun trasferimento.</p></Card>
      ) : (
        <div className="flex flex-col gap-2">
          {items.map((tr) => {
            const [label, tone] = TRANSFER_STATUS[tr.status] || [tr.status, 'neutral'];
            const qty = (tr.items || []).reduce((s, i) => s + (i.quantityRequested || 0), 0);
            return (
              <Card key={tr._id} className="flex cursor-pointer items-center justify-between gap-3 hover:shadow-elevated" onClick={() => setDetailId(tr._id)}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2"><Badge tone={tone}>{label}</Badge><span className="text-sm text-ink">{storeName(tr.fromStoreId)} → {storeName(tr.toStoreId)}</span></div>
                  <p className="mt-0.5 text-xs text-ink-muted">{(tr.items || []).length} articoli, {qty} pezzi</p>
                </div>
                {canUpdate && (
                  <div className="flex shrink-0 gap-2" onClick={(e) => e.stopPropagation()}>
                    {['requested', 'draft'].includes(tr.status) && <Button size="sm" variant="secondary" loading={act.isPending} onClick={() => act.mutate({ id: tr._id, action: 'ship' })}>Spedisci</Button>}
                    {tr.status === 'in_transit' && <Button size="sm" loading={act.isPending} onClick={() => act.mutate({ id: tr._id, action: 'receive' })}>Ricevi</Button>}
                    {['requested', 'draft'].includes(tr.status) && <button type="button" className="text-xs text-danger hover:underline" onClick={() => act.mutate({ id: tr._id, action: 'cancel' })}>Annulla</button>}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
      <TransferFormModal open={formOpen} onClose={() => setFormOpen(false)} />
      <LogisticsDetailModal open={Boolean(detailId)} kind="transfer" id={detailId} onClose={() => setDetailId(null)} />
    </div>
  );
}

function LoadsTab({ canCreate, canUpdate, canDelete }) {
  const queryClient = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const query = useQuery({ queryKey: ['loads', 'list'], queryFn: () => logisticsApi.listLoads({ limit: 50 }) });
  const items = (query.data && query.data.items) || [];
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['loads'] });

  const act = useMutation({
    mutationFn: ({ id, action }) => (action === 'confirm' ? logisticsApi.confirmLoad(id) : action === 'cancel' ? logisticsApi.cancelLoad(id) : logisticsApi.removeLoad(id)),
    onSuccess: () => { toast.success('Carico aggiornato.'); invalidate(); },
    onError: (err) => toast.error(err.message || t('common.error'))
  });

  return (
    <div className="flex flex-col gap-4">
      {canCreate && <div className="flex justify-end"><Button onClick={() => setFormOpen(true)}>Nuovo carico</Button></div>}
      {query.isLoading ? <Skeleton className="h-40 w-full" /> : items.length === 0 ? (
        <Card><p className="text-sm text-ink-muted">Nessun carico.</p></Card>
      ) : (
        <div className="flex flex-col gap-2">
          {items.map((ld) => {
            const [label, tone] = LOAD_STATUS[ld.status] || [ld.status, 'neutral'];
            const qty = (ld.items || []).reduce((s, i) => s + (i.quantity || 0), 0);
            return (
              <Card key={ld._id} className="flex cursor-pointer items-center justify-between gap-3 hover:shadow-elevated" onClick={() => setDetailId(ld._id)}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2"><Badge tone={tone}>{label}</Badge><span className="text-sm text-ink">{storeName(ld.storeId)}</span>{ld.supplier && <span className="text-xs text-ink-muted">{ld.supplier}</span>}</div>
                  <p className="mt-0.5 text-xs text-ink-muted">{(ld.items || []).length} articoli, {qty} pezzi{ld.reference ? ` - rif. ${ld.reference}` : ''}</p>
                </div>
                {canUpdate && (
                  <div className="flex shrink-0 gap-2" onClick={(e) => e.stopPropagation()}>
                    {ld.status === 'draft' && <Button size="sm" loading={act.isPending} onClick={() => act.mutate({ id: ld._id, action: 'confirm' })}>Conferma</Button>}
                    {ld.status === 'draft' && <button type="button" className="text-xs text-ink-muted hover:text-ink" onClick={() => act.mutate({ id: ld._id, action: 'cancel' })}>Annulla</button>}
                    {ld.status !== 'confirmed' && canDelete && <button type="button" className="text-xs text-danger hover:underline" onClick={() => act.mutate({ id: ld._id, action: 'delete' })}>Elimina</button>}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
      <LoadFormModal open={formOpen} onClose={() => setFormOpen(false)} />
      <LogisticsDetailModal open={Boolean(detailId)} kind="load" id={detailId} onClose={() => setDetailId(null)} />
    </div>
  );
}

export function LogisticsPage() {
  const { hasPermission } = useAuth();
  const canCreate = hasPermission('logistics:create');
  const canUpdate = hasPermission('logistics:update');
  const canDelete = hasPermission('logistics:delete');
  const [tab, setTab] = useState('transfers');

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Logistica" description="Trasferimenti tra negozi e carichi merce. Le giacenze si aggiornano in automatico." />
      <div className="flex gap-2 border-b border-border">
        {[['transfers', 'Trasferimenti'], ['loads', 'Carichi']].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setTab(k)} className={`-mb-px border-b-2 px-3 py-2 text-sm ${tab === k ? 'border-ink font-medium text-ink' : 'border-transparent text-ink-muted hover:text-ink'}`}>{l}</button>
        ))}
      </div>
      {tab === 'transfers' ? <TransfersTab canCreate={canCreate} canUpdate={canUpdate} /> : <LoadsTab canCreate={canCreate} canUpdate={canUpdate} canDelete={canDelete} />}
    </div>
  );
}
